/**
 * Money, as an integer.
 *
 * The Electron schema this replaces stored every amount as a SQLite `real`:
 * `price`, `total_price`, `vat_amount`, and eight denormalised shift-summary
 * columns. Тugrik has no minor unit in daily use, so a price is an integer
 * already and the float looks harmless. It is not, for two reasons. VAT is
 * 10% *inclusive*, so the tax on a line is `total / 11` and is fractional by
 * construction. And `summary_total` is a running sum of hundreds of those
 * across a shift — which is the number a manager counts cash against.
 *
 * So: a `Money` is a count of minor units at `SCALE`, held as a `bigint`, and
 * every operation that can lose a fraction names its rounding mode. There is
 * no `number` in the money path. `bigint` rather than `number` is not about
 * range — a day's takings fit in a double with room to spare — it is that
 * `bigint` has no silent fractional result to produce in the first place.
 */

/** Minor units per major unit, as a power of ten. ₮1.00 is 100 minor units. */
export const SCALE = 2

const FACTOR = 10n ** BigInt(SCALE)

/**
 * A signed count of minor units.
 *
 * Branded so a raw `bigint` cannot wander into the money path — `add(qty, x)`
 * should not compile. Arithmetic goes through the helpers below, which is the
 * point: every one of them that can round says how.
 *
 * The brand is a string-keyed property rather than a `unique symbol` for one
 * unglamorous reason: an unexported symbol cannot be *named* in a declaration
 * file, so every consumer that re-exported a `Money` failed to emit types with
 * TS4023 — "has or is using name 'MoneyBrand' ... but cannot be named". A
 * `bigint` intersection is already unforgeable by accident; the brand only has
 * to be distinct, not secret.
 */
export type Money = bigint & { readonly __money: 'Money' }

export const ZERO = 0n as Money

/** Wrap a count of minor units that is already known to be one. */
export const fromMinor = (units: bigint | number): Money => BigInt(units) as Money

/** The count of minor units, for a wire format or a database column. */
export const toMinor = (m: Money): bigint => m

/**
 * Build from a major-unit amount — ₮4,500 is `major(4500)`.
 *
 * Refuses a non-integer rather than rounding it, because a caller writing
 * `major(4500.5)` has a scale confusion and silently accepting it buries the
 * bug one layer further from where it was written. Use `parse` for input that
 * genuinely has a fractional part.
 */
export function major(amount: number): Money {
  if (!Number.isInteger(amount)) {
    throw new RangeError(`major() takes whole units; got ${amount}. Use parse() for a fractional amount.`)
  }
  return (BigInt(amount) * FACTOR) as Money
}

/**
 * Parse a decimal string or number at `SCALE`.
 *
 * The 1C feed sends prices as JSON numbers, so this is the boundary where a
 * float becomes an integer and it is the only place a float is allowed to
 * touch money. Anything beyond `SCALE` digits is refused, not truncated: a
 * feed that started sending three decimals is a change somebody has to see.
 */
export function parse(input: string | number): Money {
  const text = typeof input === 'number' ? formatNumber(input) : input.trim()
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text)
  if (!match) throw new RangeError(`not a decimal amount: ${JSON.stringify(input)}`)
  const [, sign, whole, fraction = ''] = match
  if (fraction.length > SCALE) {
    throw new RangeError(`${text} has more than ${SCALE} decimal places; refusing to truncate money`)
  }
  const padded = fraction.padEnd(SCALE, '0')
  const units = BigInt(whole!) * FACTOR + BigInt(padded || '0')
  return (sign === '-' ? -units : units) as Money
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) throw new RangeError(`not a finite amount: ${n}`)
  // toFixed at SCALE+1 then trim, so 0.1+0.2 arrives as 0.30 rather than
  // 0.30000000000000004 and is refused for having too many places.
  const fixed = n.toFixed(SCALE)
  return fixed
}

export const add = (a: Money, b: Money): Money => (a + b) as Money
export const sub = (a: Money, b: Money): Money => (a - b) as Money
export const neg = (a: Money): Money => -a as Money
export const abs = (a: Money): Money => (a < 0n ? -a : a) as Money

/** Multiply by a whole count — a line quantity. Exact, so no rounding mode. */
export const times = (a: Money, count: number | bigint): Money => (a * BigInt(count)) as Money

export const sum = (amounts: readonly Money[]): Money => amounts.reduce<Money>((total, m) => add(total, m), ZERO)

export const max = (a: Money, b: Money): Money => (a > b ? a : b)
export const min = (a: Money, b: Money): Money => (a < b ? a : b)
export const isZero = (a: Money): boolean => a === 0n
export const isNegative = (a: Money): boolean => a < 0n
export const compare = (a: Money, b: Money): -1 | 0 | 1 => (a < b ? -1 : a > b ? 1 : 0)

/**
 * How a fraction is disposed of. Every operation that can produce one takes
 * this explicitly — there is no default, because the default is what nobody
 * reviews.
 */
export type Rounding = 'half-up' | 'half-even' | 'down' | 'up'

/** Divide `numerator/denominator` in integer space, rounding as named. */
function divRound(numerator: bigint, denominator: bigint, mode: Rounding): bigint {
  if (denominator === 0n) throw new RangeError('division by zero in the money path')
  const negative = numerator < 0n !== denominator < 0n
  const n = numerator < 0n ? -numerator : numerator
  const d = denominator < 0n ? -denominator : denominator
  const quotient = n / d
  const remainder = n % d

  let rounded: bigint
  if (remainder === 0n) {
    rounded = quotient
  } else {
    switch (mode) {
      case 'down':
        rounded = quotient
        break
      case 'up':
        rounded = quotient + 1n
        break
      case 'half-up':
        rounded = remainder * 2n >= d ? quotient + 1n : quotient
        break
      case 'half-even': {
        const twice = remainder * 2n
        if (twice > d) rounded = quotient + 1n
        else if (twice < d) rounded = quotient
        else rounded = quotient % 2n === 0n ? quotient : quotient + 1n
        break
      }
    }
  }
  return negative ? -rounded : rounded
}

/**
 * Apply a rate expressed as a ratio of integers — 10% is `rate(m, 10, 100)`.
 *
 * A rate is never a float here. `0.1` is not representable and a discount that
 * is 10% on one line and 10.000000000000001% on the next is a receipt whose
 * total does not match the sum of its lines.
 */
export const rate = (m: Money, numerator: bigint | number, denominator: bigint | number, mode: Rounding): Money =>
  divRound(m * BigInt(numerator), BigInt(denominator), mode) as Money

/** A percentage in basis points — 1250 is 12.5%. */
export const percent = (m: Money, basisPoints: bigint | number, mode: Rounding = 'half-up'): Money =>
  rate(m, basisPoints, 10_000, mode)

/**
 * Split an amount into `parts` shares that sum back to exactly the amount.
 *
 * The largest-remainder method: every share gets the floor, then the leftover
 * minor units are handed out one each from the front. `allocate(₮10.00, 3)` is
 * `[3.34, 3.33, 3.33]` and not three times `3.33` with a cent nobody owns.
 */
export function allocate(m: Money, parts: number): Money[] {
  if (!Number.isInteger(parts) || parts < 1) throw new RangeError(`allocate needs a positive part count, got ${parts}`)
  const n = BigInt(parts)
  const base = divRound(m, n, 'down')
  const shares = Array.from({ length: parts }, () => base)
  let remainder = m - base * n
  const step = remainder < 0n ? -1n : 1n
  for (let i = 0; remainder !== 0n; i = (i + 1) % parts) {
    shares[i] = shares[i]! + step
    remainder -= step
  }
  return shares as Money[]
}

/**
 * Split by weights that sum back to exactly the amount.
 *
 * Used to spread an order-level discount across its lines so each line's share
 * is proportional and the shares still total the discount. Same
 * largest-remainder rule; ties go to the earlier line, which is arbitrary and
 * stable, which is what matters for a receipt reprinted a month later.
 */
export function allocateBy(m: Money, weights: readonly (bigint | number)[]): Money[] {
  const w = weights.map((x) => BigInt(x))
  const total = w.reduce((a, b) => a + b, 0n)
  if (total === 0n) return allocate(m, Math.max(1, w.length)).slice(0, w.length)
  const shares = w.map((weight) => divRound(m * weight, total, 'down'))
  let remainder = m - shares.reduce((a, b) => a + b, 0n)
  const order = w
    .map((weight, i) => ({ i, rem: (m * weight) % total }))
    .sort((a, b) => (b.rem > a.rem ? 1 : b.rem < a.rem ? -1 : a.i - b.i))
  const step = remainder < 0n ? -1n : 1n
  for (let k = 0; remainder !== 0n; k = (k + 1) % order.length) {
    const idx = order[k]!.i
    shares[idx] = shares[idx]! + step
    remainder -= step
  }
  return shares as Money[]
}

/**
 * Split a VAT-inclusive amount into net and tax.
 *
 * Mongolia's VAT is 10% and prices on a menu include it, so the tax in ₮1,100
 * is ₮100 — `gross * rate / (100 + rate)`. Half-up, and the net is the
 * *remainder* rather than a second rounding, so `net + vat === gross` holds
 * for every input. That identity is the whole reason this function exists
 * rather than two calls at the call site.
 */
export function splitVatInclusive(gross: Money, ratePercent: bigint | number = 10): { net: Money; vat: Money } {
  const r = BigInt(ratePercent)
  const vat = rate(gross, r, 100n + r, 'half-up')
  return { net: sub(gross, vat), vat }
}

/** Add VAT on top of a net amount. The inverse of the above, for completeness. */
export function addVat(net: Money, ratePercent: bigint | number = 10): { gross: Money; vat: Money } {
  const vat = percent(net, BigInt(ratePercent) * 100n, 'half-up')
  return { gross: add(net, vat), vat }
}

/**
 * Whole tugrik, for the systems that cannot take a minor unit.
 *
 * A card terminal and a QPay invoice take an integer number of tugrik. 1C does
 * not have to — see `toTugrik`. Each of those conversions used to be written inline as
 * `Number(m / 100n)`, which floors — so this file's entire argument, that money
 * never silently loses a fraction, held everywhere except the three places
 * money actually leaves the system.
 *
 * It **throws** rather than rounding, for the same reason `parse` refuses a
 * third decimal place: a caller handing this a fraction has a bug one layer up,
 * and rounding it here buries that bug inside a customer's charge. Retail
 * prices are whole tugrik (the möngö is long defunct), so a fraction reaching
 * this function means a discount or a split produced one and the rounding
 * belongs *there*, where it can be reasoned about.
 *
 * **Only ever hand this a gross payable.** VAT is `gross / 11` and fractional
 * by construction — see `splitVatInclusive` — and e-Barimt is specified to take
 * a real number for the tax fields. Routing a VAT amount through here would
 * turn a correct receipt into a failed sale.
 */
export function toWholeTugrik(m: Money): number {
  if (m % FACTOR !== 0n) {
    throw new RangeError(`${toDecimalString(m)} is not a whole tugrik; round where the fraction was created, not here`)
  }
  return Number(m / FACTOR)
}

/**
 * The whole tugrik a card terminal or a QPay invoice charges for an amount.
 *
 * Rounded **up**: a ₮29.70 basket is a ₮30 charge. Both take an integer and
 * neither can be asked for less than the order is worth, so the customer pays
 * the extra ₮0.30 rather than the shop losing it. The payment is recorded at
 * what was charged, not at the total, so the terminal's batch, QPay's
 * settlement and this system agree — and change, which is cash only, does not
 * count it (`totalsOf`, `changeGiven`).
 */
export function roundUpToTugrik(m: Money): Money {
  const fraction = m % FACTOR
  return (fraction <= 0n ? m : m + (FACTOR - fraction)) as Money
}

/**
 * Tugrik as a decimal number, for 1C, which takes a fraction.
 *
 * `CreateTalon` was fed through `toWholeTugrik` on the assumption that 1C, like
 * a card terminal, takes whole tugrik. It does not need to: its own SKU feed
 * sends `discounted_price: 9.9`, and a reserve at `summa: 9.9` was accepted and
 * cancelled cleanly on `Mega_amar`, 2026-09-28. Refusing the fraction here
 * stopped every basket with that promotion in it before 1C was asked.
 *
 * Through the decimal string, so the number is the shortest one that prints
 * back as the same amount. **Do the arithmetic on `Money` first**: `9.9 * 3` is
 * `29.700000000000003`, and a total like that is exactly the ₮1 disagreement
 * this file exists to prevent.
 */
export function toTugrik(m: Money): number {
  return Number(toDecimalString(m))
}

/** A plain decimal string at `SCALE`. The wire and log format; never the UI's. */
export function toDecimalString(m: Money): string {
  const negative = m < 0n
  const units = negative ? -m : m
  const whole = units / FACTOR
  const fraction = (units % FACTOR).toString().padStart(SCALE, '0')
  return `${negative ? '-' : ''}${whole}.${fraction}`
}

/**
 * For a screen or a receipt.
 *
 * Тugrik is written without decimals when it has none, because a menu that
 * says «4,500.00₮» reads as a foreign currency to the person buying a coffee.
 * The fraction appears only when it is non-zero, which on a menu is never and
 * on a VAT line is sometimes.
 */
export function format(m: Money, options: { symbol?: boolean; locale?: string } = {}): string {
  const { symbol = true, locale = 'mn-MN' } = options
  const negative = m < 0n
  const units = negative ? -m : m
  const whole = units / FACTOR
  const fraction = units % FACTOR
  const grouped = new Intl.NumberFormat(locale === 'mn-MN' ? 'en-US' : locale).format(whole)
  const body = fraction === 0n ? grouped : `${grouped}.${fraction.toString().padStart(SCALE, '0')}`
  return `${negative ? '-' : ''}${body}${symbol ? '₮' : ''}`
}
